import React, { useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, PlusCircle, MinusCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { downloadPdf } from "./FileUpload";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const ACTION = {
  update: { cls: "text-palm", Icon: CheckCircle2, label: "update" },
  create: { cls: "text-crimson", Icon: PlusCircle, label: "create new" },
  unchanged: { cls: "text-muted-foreground", Icon: MinusCircle, label: "no change" },
  error: { cls: "text-crimson", Icon: AlertTriangle, label: "error" },
};

const fmt = (v) => (typeof v === "object" && v !== null ? Object.values(v).filter(Boolean).join(" / ") : String(v));

function ChangeCell({ r, diff }) {
  if (r.action === "error") return <span className="text-crimson">{r.problems.join(", ")}</span>;
  if (diff.length === 0) return <span className="text-muted-foreground">—</span>;
  return <ul className="space-y-0.5">{diff.map(([k, v]) => <li key={k}><span className="font-mono-accent text-muted-foreground">{k}:</span> {fmt(v)}</li>)}</ul>;
}


const PreviewRow = ({ r }) => {
  const { cls, Icon, label } = ACTION[r.action];
  const diff = Object.entries(r.changes || {});
  return (
    <tr className="border-b border-gold/15 align-top text-xs" data-testid={`bulk-update-row-${r.line}`}>
      <td className="py-1.5 pr-2 text-muted-foreground">{r.line}</td>
      <td className="py-1.5 pr-2"><div className="font-medium">{r.name || "—"}</div><div className="font-mono-accent text-[10px] text-muted-foreground">{r.member_id || r.email}{r.matched_by && ` · by ${r.matched_by}`}</div></td>
      <td className="py-1.5 pr-2"><ChangeCell r={r} diff={diff} /></td>
      <td className={`py-1.5 ${cls}`}><span className="flex items-center gap-1 whitespace-nowrap"><Icon size={12} /> {label}</span></td>
    </tr>
  );
};

const Results = ({ result, onClose }) => {
  const failed = result.results.filter((r) => r.status === "failed");
  return (
    <div className="space-y-3" data-testid="bulk-update-results">
      <div className="grid grid-cols-4 gap-2 text-center text-sm">
        {[["updated", "palm"], ["created", "crimson"], ["unchanged", "gold"], ["failed", "crimson"]].map(([k, c]) => (
          <div key={k} className={`rounded-lg bg-${c}/10 p-2`}><p className={`text-2xl font-bold text-${c}`} data-testid={`bulk-update-${k}`}>{result.summary[k]}</p>{k}</div>
        ))}
      </div>
      {failed.length > 0 && (
        <ul className="max-h-40 overflow-auto rounded-lg border border-crimson/30 p-2 text-xs">
          {failed.map((r) => <li key={r.member_id || r.email}><b>{r.member_id || r.email}</b>: {r.reason}</li>)}
        </ul>
      )}
      <Button onClick={onClose} className="w-full bg-crimson" data-testid="bulk-update-done">Done</Button>
    </div>
  );
};

const UploadStep = ({ busy, onPick }) => {
  const fileRef = useRef();
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Upload an Excel (.xlsx) or CSV file. Rows are matched by <code className="font-mono-accent">member_id</code> first, then <code className="font-mono-accent">email</code>. Blank cells leave the current value unchanged; rows that match nobody are created as new members (first and last name required). Download the current roster, edit the registration fields, and upload it back.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => downloadPdf("/admin/bulk-update/export.xlsx", "members-current.xlsx")} data-testid="bulk-update-export-btn"><Download size={15} className="mr-1" /> Download current roster (.xlsx)</Button>
        <Button variant="outline" onClick={() => downloadPdf("/admin/bulk-update/template.xlsx", "member-bulk-update-template.xlsx")} data-testid="bulk-update-template-btn"><Download size={15} className="mr-1" /> Blank template</Button>
        <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-crimson" data-testid="bulk-update-upload-btn">{busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Upload size={15} className="mr-1" />} Choose file</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="hidden" onChange={onPick} data-testid="bulk-update-file-input" />
      </div>
    </div>
  );
};

const PreviewStep = ({ preview, busy, onBack, onApply }) => {
  const applicable = preview.summary.update + preview.summary.create;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="bulk-update-summary">
        <span className="font-semibold text-palm">{preview.summary.update} to update</span>
        <span className="font-semibold text-crimson">{preview.summary.create} to create</span>
        <span className="font-semibold text-muted-foreground">{preview.summary.unchanged} unchanged</span>
        <span className="font-semibold text-crimson">{preview.summary.error} with errors</span>
      </div>
      <div className="max-h-80 overflow-auto rounded-lg border border-gold/20">
        <table className="w-full text-left"><thead className="sticky top-0 bg-parchment-card text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-1.5">#</th><th className="p-1.5">Member</th><th className="p-1.5">Changes</th><th className="p-1.5">Action</th></tr></thead>
          <tbody>{preview.rows.map((r) => <PreviewRow key={r.line} r={r} />)}</tbody></table>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} data-testid="bulk-update-back">Choose another file</Button>
        <Button onClick={onApply} disabled={busy || applicable === 0} className="flex-1 bg-crimson" data-testid="bulk-update-apply-btn">{busy && <Loader2 size={15} className="mr-1 animate-spin" />} Apply {applicable} change(s)</Button>
      </div>
    </div>
  );
};

export const BulkUpdate = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setPreview(null); setResult(null); };

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/bulk-update/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const apply = async () => {
    const rows = preview.rows.filter((r) => r.action === "update" || r.action === "create").map(({ member_id, email, action, changes }) => ({ member_id, email, action, changes }));
    setBusy(true);
    try { const { data } = await api.post("/admin/bulk-update/apply", { rows }); setResult(data); toast.success(`${data.summary.updated} updated, ${data.summary.created} created`); onDone?.(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="bulk-update-btn"><FileSpreadsheet size={15} /> Update from Excel</Button>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="bulk-update-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">Bulk update member records</DialogTitle></DialogHeader>
        {result && <Results result={result} onClose={() => setOpen(false)} />}
        {!result && preview && <PreviewStep preview={preview} busy={busy} onBack={reset} onApply={apply} />}
        {!result && !preview && <UploadStep busy={busy} onPick={pick} />}
      </DialogContent>
    </Dialog>
  );
};
